import React, { useEffect } from "react";
import PageWrapper from "../modules/PageWrapper";
import { 
  useGetTodayBirthdaysQuery,
  useGetReceivedWishesQuery
} from "../../../store/member/memberBirthdaySlice";
import { CardComp, ButtonComp } from "../../../components";
import FallbackImage from "../../../components/Common/ImageWrapper";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import moment from "moment";
import BirthdayWishModal from "../Home/components/BirthdayWishModal";

const MemberBirthdays = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("Today's Birthdays");
  const [selectedBirthday, setSelectedBirthday] = useState(null);
  const [showWishModal, setShowWishModal] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("tab") === "received") {
      setActiveTab("Wishes Received");
    }
  }, [location]);
  
  const { data: birthdayData, isLoading: isLoadingBirthdays } = useGetTodayBirthdaysQuery();
  const { data: wishesData, isLoading: isLoadingWishes } = useGetReceivedWishesQuery();

  const birthdays = birthdayData?.birthdays?.data || birthdayData?.birthdays || [];
  const wishes = wishesData?.wishes?.data || wishesData?.wishes || [];

  const handleWishClick = (member) => {
    setSelectedBirthday({
      id: member.id,
      name: `${member.first_name} ${member.last_name}`,
    });
    setShowWishModal(true);
  };

  const renderTodayBirthdays = () => {
    if (isLoadingBirthdays) {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <CardComp key={i} cardClassName="border-0 shadow-sm rounded-4">
              <TableSkeletonLoader rows={3} columns={1} />
            </CardComp>
          ))}
        </div>
      );
    }

    if (birthdays.length === 0) {
      return (
        <div className="d-flex justify-content-center align-items-center py-5 mt-4">
          <div className="col-lg-7 text-center bg-white p-5 rounded-4 shadow3 border-0 animate__animated animate__fadeIn">
            <div 
              className="mb-4 d-inline-flex align-items-center justify-content-center rounded-circle shadow-sm"
              style={{ 
                width: '120px', 
                height: '120px', 
                background: 'linear-gradient(135deg, var(--color-primary-soft) 0%, var(--color-secondary-soft) 100%)',
                fontSize: '60px'
              }}
            >
              ✨
            </div>
            <h3 className="fw-bold text-black1 mb-3">Quiet Day for Celebrations</h3>
            <p className="text-blacklight mb-5 fs-6 px-lg-5 leading-relaxed">
              It looks like no one is celebrating their birthday today. 
              Our community is taking a little break from the party, but we'll let you know as soon as the next celebration begins!
            </p>
            <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center align-items-center">
              <ButtonComp
                btnText="Back to Dashboard"
                btnClassName="text-white rounded-pill px-5 py-3 border-0 shadow-sm hover:scale-105 transition-all"
                containerClassName="w-auto"
                ButtonInlineStyle={{ backgroundColor: "var(--color-secondary)", minWidth: "220px" }}
                onClick={() => window.history.back()}
              />
              <Link to="/member/profile" className="text-decoration-none w-auto">
                <ButtonComp
                  btnText="Check Privacy Settings"
                  btnClassName="text-primary1 rounded-pill px-5 py-3 shadow-sm hover:bg-light transition-all"
                  containerClassName="w-auto"
                  BorderColor="var(--color-primary)"
                  ButtonInlineStyle={{ backgroundColor: "#ffffff", minWidth: "220px" }}
                />
              </Link>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {birthdays.map((member, i) => (
          <CardComp key={i} cardClassName="border-0 shadow-sm rounded-4 hover:shadow-md transition-all">
            <div className="p-4 text-center">
              <div className="mb-3 position-relative d-inline-block">
                <FallbackImage
                  src={member.photo || "/Staff/avatar.svg"}
                  width="80px"
                  height="80px"
                  className="rounded-circle border"
                />
                <div className="position-absolute" style={{ bottom: "-5px", right: "-5px", fontSize: "24px" }}>🎂</div>
              </div>
              <h6 className="fw-bold text-black1 mb-1">{member.first_name} {member.last_name}</h6>
              <p className="text-blacklight small mb-4">
                Membership No: {member.member_no || "N/A"}
              </p>
              <ButtonComp
                btnText="Send Birthday Wish"
                btnClassName="w-100 bg-primary1 text-white py-3 rounded-10 border-0 shadow-sm"
                onClick={() => handleWishClick(member)}
                data-tour="member-bday-wish"
              />
            </div>
          </CardComp>
        ))}
      </div>
    );
  };

  const renderReceivedWishes = () => {
    if (isLoadingWishes) {
      return (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <CardComp key={i} cardClassName="border-0 shadow-sm rounded-4">
              <TableSkeletonLoader rows={2} columns={1} />
            </CardComp>
          ))}
        </div>
      );
    }

    if (wishes.length === 0) {
      return (
        <div className="d-flex justify-content-center align-items-center py-5 mt-4">
          <div className="col-lg-7 text-center bg-white p-5 rounded-4 shadow3 border-0">
            <div 
              className="mb-4 d-inline-flex align-items-center justify-content-center rounded-circle shadow-sm"
              style={{ width: '100px', height: '100px', background: '#f8f9fa', fontSize: '40px' }}
            >
              💌
            </div>
            <h4 className="fw-bold text-black1">No wishes yet</h4>
            <p className="text-blacklight">When members send you birthday wishes, they will appear here!</p>
          </div>
        </div>
      );
    }

    return (
      <div className="row g-4">
        {wishes.map((wish, i) => (
          <div key={i} className="col-lg-6">
            <CardComp cardClassName="border-0 shadow-sm rounded-4 h-100">
              <div className="p-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="d-flex align-items-center gap-3">
                    <FallbackImage
                      src={wish.sender?.photo || "/Staff/avatar.svg"}
                      width="48px"
                      height="48px"
                      className="rounded-circle border"
                    />
                    <div>
                      <h6 className="fw-bold text-black1 mb-0">
                        {wish.sender?.first_name} {wish.sender?.last_name}
                      </h6>
                      <p className="text-blacklight small mb-0">
                        {wish.sender_type?.includes('Member') ? 'Member' : 
                         wish.sender_type?.includes('Staff') ? 'Admin' : 'System'}
                      </p>
                    </div>
                  </div>
                  <div className="text-blacklight small">
                    {moment(wish.created_at).fromNow()}
                  </div>
                </div>
                  <div 
                    className="bg-light p-3 rounded-4 border-start border-primary1 border-4 italic"
                    dangerouslySetInnerHTML={{ 
                      __html: wish.message ? wish.message.replace(/^"|"$/g, '') : "" 
                    }}
                  />
              </div>
            </CardComp>
          </div>
        ))}
      </div>
    );
  };

  const tabs = ["Today's Birthdays", "Wishes Received"];

  return (
    <PageWrapper title="Birthdays">
      <div className="py-4" data-tour="member-bday-list">
        {/* Tab Navigation */}
        <div className="d-flex gap-4 mb-4 border-bottom">
          {tabs.map((tab) => (
            <div
              key={tab}
              className={`pb-2 pointer fw-bold transition-all ${
                activeTab === tab
                  ? "text-primary1 border-bottom border-primary1 border-3"
                  : "text-blacklight hover:text-black1"
              }`}
              onClick={() => setActiveTab(tab)}
              style={{ fontSize: "16px" }}
            >
              {tab}
            </div>
          ))}
        </div>

        {activeTab === "Today's Birthdays" ? renderTodayBirthdays() : renderReceivedWishes()}
      </div>

      <BirthdayWishModal
        show={showWishModal}
        onHide={() => setShowWishModal(false)}
        recipient={selectedBirthday}
      />
    </PageWrapper>
  );
};

export default MemberBirthdays;

